/**
 * ============================================================================
 * DOGSTER BARF · Calculadora embebida en producto (v2 — simplificada)
 * ----------------------------------------------------------------------------
 * Formulario de una sola pantalla (sin asistente paso a paso): menús
 * desplegables para Ciudad / Edad / Tamaño / Actividad, selección con
 * íconos para Perro/Gato, y un campo numérico para el peso real de la
 * mascota. Al hacer clic en "Calcular" se muestra el resumen y las
 * promociones de 14 y 28 días.
 *
 * Metodología (igual que el Excel, confirmada con Diego):
 *   1. % del peso corporal según especie / edad / tamaño / actividad.
 *   2. gramos/día = peso_kg * 1000 * %
 *   3. bolsas/semana = CEIL( (gramos/día * 7) / 250 ), con un piso de
 *      4 bolsas (1 kilo, venta mínima). Por encima del piso NUNCA se
 *      redondea a kilos completos — sube bolsa por bolsa.
 *   4. El precio de cada bolsa se lee EN VIVO de la variante de Shopify
 *      correspondiente a la Ciudad elegida (debe estar configurado como
 *      precio POR BOLSA de 250 g, con "Precio unitario" activado en
 *      Shopify para mostrar el $/kg de referencia).
 *   5. Promociones: 14 días = 2× las bolsas de la semana con descuento;
 *      28 días = 4× las bolsas de la semana con descuento. Los % viven en
 *      barf-calculator-config.js (discounts.dias14 / discounts.dias28).
 * ============================================================================
 */
(function () {
  'use strict';

  const CFG = window.DOGSTER_BARF_CONFIG;

  class BarfProductCalculator {
    constructor(root) {
      this.root = root;
      this.productData = this._parseProductData(root);
      this.cityVariants = this._buildCityVariants(this.productData);
      this.contact = this._parseContactSettings(root);

      this.state = {
        cityId: null,
        species: null,        // 'perro' | 'gato'
        ageCategory: null,    // 'adulto' | 'cachorro'
        ageMonths: null,      // solo si cachorro
        ageBracket: null,     // '2-4' | '5-8' | '9-11' (derivado)
        size: null,           // 'gigante' | 'grande' | 'mediano' | 'pequeno'
        activity: null,       // 'activa' | 'sedentaria'
        weightKg: null
      };

      this._render();
      this._bindEvents();
    }

    // ------------------------------------------------------------------
    // Datos de Shopify
    // ------------------------------------------------------------------
    _parseProductData(root) {
      const el = root.querySelector('[data-barf-product-data]');
      try { return JSON.parse(el.textContent); } catch (e) { return { ciudadOptionIndex: -1, variants: [] }; }
    }

    _buildCityVariants(data) {
      const idx = data.ciudadOptionIndex;
      const key = 'option' + (idx + 1);
      const map = {};
      const order = [];
      if (idx < 0) return { map, order };
      data.variants.forEach((v) => {
        const cityLabel = v[key];
        if (!cityLabel) return;
        if (!map[cityLabel]) { map[cityLabel] = v; order.push(cityLabel); }
      });
      return { map, order };
    }

    _parseContactSettings(root) {
      const el = root.querySelector('[data-barf-contact-settings]');
      let fromTheme = {};
      try { fromTheme = JSON.parse(el.textContent); } catch (e) { fromTheme = {}; }
      return {
        whatsappNumber: fromTheme.whatsappNumber || CFG.contact.whatsappNumber,
        email: fromTheme.contactEmail || CFG.contact.email
      };
    }

    // ==================================================================
    // RENDER (una sola pantalla)
    // ==================================================================
    _render() {
      const cityOptions = this.cityVariants.order.map((c) => `<option value="${c}">${c}</option>`).join('');

      this.root.innerHTML = `
        <form class="barf2-form" novalidate>
          <div class="barf2-field">
            <label class="barf2-label" for="barf2-city">1. Selecciona tu ciudad</label>
            <select class="barf2-select" id="barf2-city" data-field="city" required>
              <option value="" disabled selected>Elige una ciudad</option>
              ${cityOptions}
            </select>
          </div>

          <div class="barf2-field">
            <p class="barf2-label">2. ¿Qué peludito tienes?</p>
            <div class="barf2-species-row">
              <button type="button" class="barf2-species-btn" data-field="species" data-value="perro">
                <span class="barf2-species-icon" aria-hidden="true">🐶</span>
                <span>Perro</span>
              </button>
              <button type="button" class="barf2-species-btn" data-field="species" data-value="gato">
                <span class="barf2-species-icon" aria-hidden="true">🐱</span>
                <span>Gato</span>
              </button>
            </div>
          </div>

          <div class="barf2-field" data-group="age" hidden>
            <label class="barf2-label" data-age-label for="barf2-age-category">3. Tu <span data-species-word>perro</span> es</label>
            <select class="barf2-select" id="barf2-age-category" data-field="age-category">
              <option value="" disabled selected>Elige una opción</option>
              <option value="adulto">Adulto</option>
              <option value="cachorro">Cachorro</option>
            </select>
            <div class="barf2-subfield" data-group="age-months" hidden>
              <label class="barf2-label" for="barf2-age-months">¿Cuántos meses tiene?</label>
              <input type="number" class="barf2-input" id="barf2-age-months" data-field="age-months" min="2" max="11" step="1" placeholder="Ej: 6">
              <p class="barf2-hint" data-age-bracket-hint></p>
            </div>
          </div>

          <div class="barf2-field" data-group="size" hidden>
            <label class="barf2-label" for="barf2-size">4. Elige el tamaño</label>
            <select class="barf2-select" id="barf2-size" data-field="size">
              <option value="" disabled selected>Elige un tamaño</option>
            </select>
          </div>

          <div class="barf2-field" data-group="activity" hidden>
            <label class="barf2-label" for="barf2-activity">5. Actividad física</label>
            <select class="barf2-select" id="barf2-activity" data-field="activity">
              <option value="" disabled selected>Elige una opción</option>
              <option value="activa">Sí, es activa — paseos largos, ejercicio frecuente y mucha energía</option>
              <option value="sedentaria">No, es tranquila — rutina más calmada o sedentaria</option>
            </select>
          </div>

          <div class="barf2-field" data-group="weight" hidden>
            <label class="barf2-label" for="barf2-weight">6. Peso de mi peludo (kg)</label>
            <input type="number" class="barf2-input" id="barf2-weight" data-field="weight" min="0.3" max="120" step="0.1" placeholder="Ej: 21.5">
          </div>

          <button type="button" class="barf2-btn barf2-btn--primary barf2-btn--full" data-action="calculate" disabled>
            Calcular
          </button>
        </form>

        <div class="barf2-results" data-results hidden></div>
        <div class="barf2-feedback" data-feedback role="status" aria-live="polite"></div>
      `;
    }

    // ------------------------------------------------------------------
    // Mostrar/ocultar campos según lo que ya se respondió
    // ------------------------------------------------------------------
    _updateVisibility() {
      const s = this.state;
      const show = (group, visible) => {
        const el = this.root.querySelector(`[data-group="${group}"]`);
        if (el) el.hidden = !visible;
      };

      show('age', !!s.species);
      show('size', s.ageCategory === 'adulto');
      show('activity', s.species === 'perro' && s.ageCategory === 'adulto');
      show('weight', !!s.species && !!s.ageCategory && (s.ageCategory !== 'cachorro' || s.ageMonths));

      const monthsGroup = this.root.querySelector('[data-group="age-months"]');
      if (monthsGroup) monthsGroup.hidden = s.ageCategory !== 'cachorro';

      const speciesWord = this.root.querySelector('[data-species-word]');
      if (speciesWord) speciesWord.textContent = s.species === 'gato' ? 'gato' : 'perro';

      if (s.ageCategory === 'adulto' && s.species) {
        this._populateSizeOptions();
      }

      this._validateCalculateButton();
    }

    _populateSizeOptions() {
      const sizeSelect = this.root.querySelector('#barf2-size');
      if (!sizeSelect) return;
      const ranges = CFG.sizeRanges[this.state.species];
      const current = sizeSelect.value;
      sizeSelect.innerHTML = `<option value="" disabled ${!current ? 'selected' : ''}>Elige un tamaño</option>` +
        Object.keys(ranges).map((key) => `<option value="${key}" ${current === key ? 'selected' : ''}>${ranges[key].label}</option>`).join('');
    }

    _resolveAgeBracket(months) {
      const m = Math.max(2, Math.min(11, months));
      if (m <= 4) return '2-4';
      if (m <= 8) return '5-8';
      return '9-11';
    }

    _validateCalculateButton() {
      const s = this.state;
      const btn = this.root.querySelector('[data-action="calculate"]');
      if (!btn) return;
      let valid = !!(s.cityId && s.species && s.ageCategory && s.weightKg > 0);
      if (s.ageCategory === 'cachorro') valid = valid && s.ageMonths > 0;
      if (s.ageCategory === 'adulto') valid = valid && !!s.size;
      if (s.species === 'perro' && s.ageCategory === 'adulto') valid = valid && !!s.activity;
      btn.disabled = !valid;
    }

    // ------------------------------------------------------------------
    // Eventos
    // ------------------------------------------------------------------
    _bindEvents() {
      this.root.addEventListener('click', (e) => {
        const speciesBtn = e.target.closest('[data-field="species"]');
        if (speciesBtn) {
          this.state.species = speciesBtn.dataset.value;
          this.root.querySelectorAll('[data-field="species"]').forEach((b) => b.classList.toggle('is-selected', b === speciesBtn));
          // reset campos dependientes
          this.state.ageCategory = null; this.state.size = null; this.state.activity = null;
          this._updateVisibility();
          return;
        }
        const calcBtn = e.target.closest('[data-action="calculate"]');
        if (calcBtn) { this._showResults(); return; }

        const cartBtn = e.target.closest('[data-action="add-to-cart"]');
        if (cartBtn) { this._addToCart(cartBtn.dataset.plan, cartBtn); return; }

        const waBtn = e.target.closest('[data-action="contact-whatsapp"]');
        if (waBtn) { this._contactWhatsApp(); return; }
      });

      this.root.addEventListener('change', (e) => {
        if (e.target.matches('[data-field="city"]')) { this.state.cityId = e.target.value; this._validateCalculateButton(); }
        if (e.target.matches('[data-field="age-category"]')) {
          this.state.ageCategory = e.target.value;
          this.state.size = null; this.state.ageMonths = null;
          this._updateVisibility();
        }
        if (e.target.matches('[data-field="size"]')) { this.state.size = e.target.value; this._validateCalculateButton(); }
        if (e.target.matches('[data-field="activity"]')) { this.state.activity = e.target.value; this._validateCalculateButton(); }
      });

      this.root.addEventListener('input', (e) => {
        if (e.target.matches('[data-field="age-months"]')) {
          const v = parseInt(e.target.value, 10);
          this.state.ageMonths = v > 0 ? v : null;
          const hint = this.root.querySelector('[data-age-bracket-hint]');
          if (this.state.ageMonths) {
            this.state.ageBracket = this._resolveAgeBracket(this.state.ageMonths);
            const labels = { '2-4': '2 a 4 meses', '5-8': '5 a 8 meses', '9-11': '9 a 11 meses' };
            if (hint) hint.textContent = `Rango de edad: ${labels[this.state.ageBracket]}`;
          } else if (hint) {
            hint.textContent = '';
          }
          this._updateVisibility();
        }
        if (e.target.matches('[data-field="weight"]')) {
          const v = parseFloat(e.target.value.replace(',', '.'));
          this.state.weightKg = v > 0 ? v : null;
          this._validateCalculateButton();
        }
      });
    }

    // ------------------------------------------------------------------
    // Cálculo
    // ------------------------------------------------------------------
    _resolvePercentage() {
      const s = this.state;
      const p = CFG.percentages[s.species];
      if (s.ageCategory === 'cachorro') return p.cachorro[s.ageBracket];
      if (s.species === 'perro') return p.adulto[s.size][s.activity];
      return p.adulto[s.size];
    }

    _round(v) { return Math.round(v / CFG.roundToNearest) * CFG.roundToNearest; }
    _fmtMoney(v) { return CFG.currencySymbol + Math.round(v).toLocaleString('es-CO'); }

    _calculate() {
      const s = this.state;
      const pct = this._resolvePercentage();
      const variant = this.cityVariants.map[s.cityId];
      const pricePerBag = variant ? parseFloat(variant.price) : 0;
      const bagsPerKg = 1000 / CFG.portionGrams; // 4
      const minBags = CFG.minSaleKg * bagsPerKg;  // 4

      const dailyGrams = s.weightKg * 1000 * pct;
      const weeklyBagsRaw = Math.ceil((dailyGrams * 7) / CFG.portionGrams);
      const weeklyBags = Math.max(minBags, weeklyBagsRaw);
      const weeklyPrice = this._round(weeklyBags * pricePerBag);

      const promos = {};
      [['dias14', 2, CFG.discounts.dias14], ['dias28', 4, CFG.discounts.dias28]].forEach(([id, mult, discount]) => {
        const bags = weeklyBags * mult;
        const before = this._round(bags * pricePerBag);
        const after = this._round(bags * pricePerBag * (1 - discount));
        promos[id] = { bags, before, after, savings: before - after, discountPct: Math.round(discount * 100) };
      });

      return {
        dailyGrams: Math.round(dailyGrams),
        weeklyBags, weeklyPrice, pricePerBag,
        pricePerKgRef: pricePerBag * bagsPerKg,
        promos, variant
      };
    }

    // ------------------------------------------------------------------
    // Resultado
    // ------------------------------------------------------------------
    _showResults() {
      const r = this._calculate();
      const resultsEl = this.root.querySelector('[data-results]');
      if (!resultsEl) return;

      if (!r.variant) {
        resultsEl.hidden = false;
        resultsEl.innerHTML = `<p class="barf2-hint">No encontramos esa ciudad como variante del producto. Revisa la selección o escríbenos.</p>`;
        return;
      }

      const promoCard = (id, title) => {
        const p = r.promos[id];
        return `
        <article class="barf2-promo">
          <h4 class="barf2-promo__title">${title} <span class="barf2-promo__badge">-${p.discountPct}%</span></h4>
          <p class="barf2-promo__qty">${p.bags} bolsas de 250 g</p>
          <p class="barf2-promo__price"><s>${this._fmtMoney(p.before)}</s> ${this._fmtMoney(p.after)}</p>
          <button type="button" class="barf2-btn barf2-btn--primary barf2-btn--full" data-action="add-to-cart" data-plan="${id}">
            Agregar al carrito
          </button>
        </article>`;
      };

      resultsEl.hidden = false;
      resultsEl.innerHTML = `
        <div class="barf2-summary">
          <div class="barf2-summary__row">
            <span>Ración diaria</span>
            <strong>${r.dailyGrams.toLocaleString('es-CO')} g/día</strong>
          </div>
          <div class="barf2-summary__row">
            <span>Bolsas de 250 g a comprar</span>
            <strong>${r.weeklyBags} unidades</strong>
          </div>
          <div class="barf2-summary__row">
            <span>Valor semanal</span>
            <strong>${this._fmtMoney(r.weeklyPrice)}</strong>
          </div>
          <p class="barf2-hint">* La venta mínima es de un kilo.</p>
          <button type="button" class="barf2-btn barf2-btn--outline barf2-btn--full" data-action="add-to-cart" data-plan="semanal">
            Agregar semana al carrito
          </button>
        </div>

        <p class="barf2-promos-title">Promociones</p>
        <div class="barf2-promo-grid">
          ${promoCard('dias14', '14 días')}
          ${promoCard('dias28', '28 días')}
        </div>
      `;
      resultsEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    // ------------------------------------------------------------------
    // Carrito / contacto
    // ------------------------------------------------------------------
    _addToCart(planId, btn) {
      const r = this._calculate();
      const variant = r.variant;
      const feedback = this.root.querySelector('[data-feedback]');
      if (!variant) return;

      const quantity = planId === 'semanal' ? r.weeklyBags : r.promos[planId].bags;
      const originalLabel = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Agregando…';

      const properties = {
        'Plan': planId,
        'Peso mascota (kg)': String(this.state.weightKg),
        'Ración diaria (g)': String(r.dailyGrams)
      };

      fetch(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity, properties }] })
      })
        .then((res) => { if (!res.ok) return res.json().then((err) => Promise.reject(err)); return res.json(); })
        .then(() => {
          btn.textContent = '✓ Agregado';
          if (feedback) { feedback.classList.remove('is-error'); feedback.textContent = `${quantity} bolsas agregadas al carrito.`; }
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          setTimeout(() => { btn.disabled = false; btn.textContent = originalLabel; }, 2200);
        })
        .catch((err) => {
          btn.disabled = false; btn.textContent = originalLabel;
          if (feedback) { feedback.classList.add('is-error'); feedback.textContent = (err && err.description) || 'No pudimos agregar el producto al carrito.'; }
        });
    }

    _contactWhatsApp() {
      const number = (this.contact.whatsappNumber || '').replace(/\D/g, '');
      const feedback = this.root.querySelector('[data-feedback]');
      if (!number) {
        if (feedback) { feedback.classList.add('is-error'); feedback.textContent = 'Falta configurar el número de WhatsApp.'; }
        return;
      }
      window.open(`https://wa.me/${number}`, '_blank', 'noopener');
    }
  }

  function init() {
    document.querySelectorAll('[data-barf-product-calculator]').forEach((root) => {
      if (root.dataset.barfInitialized) return;
      root.dataset.barfInitialized = 'true';
      new BarfProductCalculator(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
